import type { AtualizarCategoriaDto, CategoriasDuplicadasResponse, CategoriasResponse, CategoriaResponse, CriarCategoriaDto, MesclarCategoriasDto, MesclarCategoriasResponse, MoverItensCategoriaDto } from '@triade/contracts';
import { useConsulta, useMutacao } from './base';

/*
 * Categorias de itens (arvore por empresa). Leitura com item.ver; gravar com item.editar.
 * Chaves: ['categorias'] cobre a arvore; mover itens tambem mexe nos itens, entao invalida ['itens'].
 */

/** Arvore de categorias (GET /categorias): `item.ver`. Lista em ordem de arvore, com `nivel`, contagens e `semCategoria`. */
export function useCategorias() {
  return useConsulta<CategoriasResponse>(['categorias'], '/categorias');
}

/** Criacao (POST /categorias; `paiId` ausente = raiz): `item.editar`. */
export function useCriarCategoria() {
  return useMutacao<CategoriaResponse, CriarCategoriaDto>({
    metodo: 'POST',
    caminho: () => '/categorias',
    corpo: (v) => v,
    invalidar: [['categorias']],
  });
}

/** Renomear e/ou mover (PATCH /categorias/:id; `paiId` null = raiz): `item.editar`. */
export function useAtualizarCategoria() {
  return useMutacao<CategoriaResponse, { id: string } & AtualizarCategoriaDto>({
    metodo: 'PATCH',
    caminho: (v) => `/categorias/${v.id}`,
    corpo: ({ id: _id, ...resto }) => resto,
    invalidar: [['categorias']],
  });
}

/** Exclusao (DELETE /categorias/:id): `item.editar`. A API responde 409 CATEGORIA_COM_ITENS ou CATEGORIA_COM_SUBCATEGORIAS. */
export function useExcluirCategoria() {
  return useMutacao<void, { id: string }>({
    metodo: 'DELETE',
    caminho: (v) => `/categorias/${v.id}`,
    invalidar: [['categorias']],
  });
}

/** Leva os itens da categoria para outra (`paraId`) ou para "sem categoria" (null): `item.editar`. Mexe nos itens. */
export function useMoverItensCategoria() {
  return useMutacao<void, { id: string } & MoverItensCategoriaDto>({
    metodo: 'POST',
    caminho: (v) => `/categorias/${v.id}/mover-itens`,
    corpo: ({ id: _id, ...resto }) => resto,
    invalidar: [['categorias'], ['itens']],
  });
}

/** Sugestoes de duplicadas (GET /categorias/duplicadas): `item.ver`. So consulta quando o dialogo esta aberto (`ativo`). */
export function useCategoriasDuplicadas(ativo = true) {
  return useConsulta<CategoriasDuplicadasResponse>(['categorias', 'duplicadas'], '/categorias/duplicadas', { ativo });
}

/** Mescla categorias (POST /categorias/mesclar): `item.editar`. Mexe nos itens e nas subcategorias. */
export function useMesclarCategorias() {
  return useMutacao<MesclarCategoriasResponse, MesclarCategoriasDto>({
    metodo: 'POST',
    caminho: () => '/categorias/mesclar',
    corpo: (v) => v,
    invalidar: [['categorias'], ['itens']],
  });
}
